import { useRef, type ReactNode } from 'react'
import { Paperclip, X } from 'lucide-react'
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  type SxProps,
  type TextFieldProps,
  type Theme,
} from '@mui/material'
import type { UploadedFileRef } from '../types/onboarding'
import { MISC_HANDLED_STATES } from '../data/miscHandledStates'
import { MISC_PRIMARY } from '../theme/onboardingTheme'

export function StepCard({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Paper
      elevation={0}
      sx={[
        {
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          p: { xs: 3, sm: 4, lg: 5 },
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Paper>
  )
}

export function StepTitle({ children, subtitle }: { children: ReactNode; subtitle?: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography component="h3" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 600, color: '#0f172a' }}>
        {children}
      </Typography>
      {subtitle ? (
        <Typography sx={{ mt: 1, fontSize: { xs: 14, sm: 15 }, lineHeight: 1.625, color: '#475569' }}>{subtitle}</Typography>
      ) : null}
    </Box>
  )
}

export function SubHeading({ children }: { children: ReactNode }) {
  return (
    <Typography component="h4" sx={{ fontSize: 15, fontWeight: 600, color: '#0f172a', mb: 1 }}>
      {children}
    </Typography>
  )
}

export function BodyText({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Typography sx={[{ fontSize: { xs: 14, sm: 15 }, lineHeight: 1.625, color: '#1e293b' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
      {children}
    </Typography>
  )
}

type NoticeTone = 'info' | 'warning' | 'success' | 'neutral' | 'error'

const NOTICE_TONES: Record<NoticeTone, { border: string; bg: string }> = {
  info: { border: '#bfdbfe', bg: '#eff6ff' },
  warning: { border: '#fde68a', bg: '#fffbeb' },
  success: { border: '#a7f3d0', bg: '#ecfdf5' },
  neutral: { border: '#e2e8f0', bg: '#f8fafc' },
  error: { border: '#fecaca', bg: '#fef2f2' },
}

export function Notice({ tone = 'info', children, sx }: { tone?: NoticeTone; children: ReactNode; sx?: SxProps<Theme> }) {
  const t = NOTICE_TONES[tone]
  return (
    <Box
      sx={[
        { borderRadius: 1, border: `1px solid ${t.border}`, bgcolor: t.bg, px: 2, py: 1.5, fontSize: 14, lineHeight: 1.6, color: '#1e293b' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  )
}

export const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 14,
    backgroundColor: '#ffffff',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: MISC_PRIMARY },
    '&.Mui-focused fieldset': { borderColor: MISC_PRIMARY },
  },
  '& .MuiInputLabel-root': { fontSize: 14 },
} as const

export function Field(props: TextFieldProps) {
  return <TextField size="small" fullWidth {...props} sx={[inputSx, ...(Array.isArray(props.sx) ? props.sx : props.sx ? [props.sx] : [])]} />
}

export function StateSelect({
  value,
  onChange,
  label,
  options,
  ...rest
}: Omit<TextFieldProps, 'onChange' | 'select' | 'value'> & {
  value: string
  onChange: (code: string) => void
  /** Limit to these codes (defaults to all states). */
  options?: readonly string[]
}) {
  const list = options ? MISC_HANDLED_STATES.filter((s) => options.includes(s.code)) : MISC_HANDLED_STATES
  return (
    <Field select label={label} value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      <MenuItem value="">
        <em>Select</em>
      </MenuItem>
      {list.map((s) => (
        <MenuItem key={s.code} value={s.code}>
          {s.code} – {s.name}
        </MenuItem>
      ))}
    </Field>
  )
}

export function toFileRef(file: File): UploadedFileRef {
  return { name: file.name, sizeBytes: file.size, uploadedAt: new Date().toISOString() }
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function FileChip({ file, onRemove }: { file: UploadedFileRef; onRemove?: () => void }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 1,
        border: '1px solid #e2e8f0',
        bgcolor: '#f8fafc',
        pl: 1.25,
        pr: onRemove ? 0.25 : 1.25,
        py: 0.25,
        fontSize: 13,
        color: '#334155',
        maxWidth: '100%',
      }}
    >
      <Paperclip size={14} aria-hidden />
      <Box component="span" title={file.name} sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file.name}
      </Box>
      <Box component="span" sx={{ color: '#94a3b8', flexShrink: 0 }}>
        {formatSize(file.sizeBytes)}
      </Box>
      {onRemove ? (
        <IconButton size="small" aria-label={`Remove ${file.name}`} onClick={onRemove}>
          <X size={14} />
        </IconButton>
      ) : null}
    </Box>
  )
}

/** Prototype upload: records file name/size only — nothing leaves the browser. */
export function UploadButton({
  label,
  accept,
  multiple = false,
  onFiles,
  variant = 'outlined',
}: {
  label: string
  accept?: string
  multiple?: boolean
  onFiles: (files: File[]) => void
  variant?: 'outlined' | 'contained'
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <Button
        type="button"
        variant={variant}
        size="small"
        onClick={() => inputRef.current?.click()}
        sx={variant === 'outlined' ? { borderColor: '#cbd5e1', color: '#334155' } : undefined}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) onFiles(files)
          e.target.value = ''
        }}
      />
    </>
  )
}

export function ProgressBar({ value, height = 8 }: { value: number; height?: number }) {
  const done = value >= 100
  return (
    <Box sx={{ height, borderRadius: 999, bgcolor: '#e2e8f0', overflow: 'hidden' }} aria-hidden>
      <Box
        sx={{
          height: '100%',
          width: `${Math.min(100, Math.max(0, value))}%`,
          borderRadius: 999,
          bgcolor: done ? '#059669' : '#3d6b7a',
          transition: 'width 400ms ease-out',
        }}
      />
    </Box>
  )
}

export function StatusPill({ status }: { status: 'Not started' | 'In progress' | 'Complete' | string }) {
  const tone =
    status === 'Complete'
      ? { bg: '#d1fae5', color: '#065f46' }
      : status === 'In progress'
        ? { bg: '#fef3c7', color: '#78350f' }
        : { bg: '#f1f5f9', color: '#475569' }
  return (
    <Box component="span" sx={{ borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 600, bgcolor: tone.bg, color: tone.color, whiteSpace: 'nowrap' }}>
      {status}
    </Box>
  )
}
